import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  Alert, AlertDescription, AlertIcon, Box, Button, ButtonGroup, Flex, FormControl, FormHelperText, FormLabel, HStack, Input, Modal, ModalBody, ModalCloseButton, ModalContent,
  ModalFooter, ModalHeader, ModalOverlay, SimpleGrid, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, VStack, useToast, IconButton, Tooltip,
} from '@chakra-ui/react'
import { Banknote, Check, Copy, Download, Eye, Send, Wallet, X } from 'lucide-react'
import { control, operatorsAdmin, type Debt, type PayoutRow, type PayoutStatus } from '@services/operators'
import { apiErrorMessage, formatDate, formatNumber, timeAgo, providerLabel } from '@utils/format'
import { ConfirmModal, EmptyState, Label, Money, PageHeader, Panel, SkeletonRows, StatTile, StatusPill, tableHead } from '@components/ui'

type Action = 'approve' | 'reject' | 'pay' | 'fail'

const TABS: Array<{ key: string; label: string; statuses: PayoutStatus[] }> = [
  { key: 'review', label: 'To review', statuses: ['requested'] },
  { key: 'send', label: 'Ready to send', statuses: ['approved'] },
  { key: 'history', label: 'History', statuses: ['paid', 'rejected', 'failed', 'cancelled'] },
  { key: 'debts', label: 'Owed to us', statuses: [] },
]

function DebtsPanel() {
  const qc = useQueryClient()
  const toast = useToast()
  const [target, setTarget] = useState<{ debt: Debt; mode: 'repayment' | 'writeoff' } | null>(null)
  const [amount, setAmount] = useState('')
  const [requestId, setRequestId] = useState('')
  const { data, isLoading } = useQuery('admin-debts', () => control.debts().then(r => r.data), { refetchInterval: 30_000 })
  const open = (debt: Debt, mode: 'repayment' | 'writeoff') => { setTarget({ debt, mode }); setAmount(String(debt.owes)); setRequestId(crypto.randomUUID()) }
  const settle = useMutation((v: { reference?: string; note?: string }) => control.settleDebt(target!.debt.operatorId, { mode: target!.mode, amount: Number(amount), requestId, ...v }), {
    onSuccess: () => { toast({ title: target?.mode === 'repayment' ? 'Repayment recorded' : 'Debt written off', status: 'success' }); setTarget(null); qc.invalidateQueries('admin-debts'); qc.invalidateQueries('control-home') },
    onError: e => { toast({ title: apiErrorMessage(e), status: 'error' }) },
  })
  const valid = Number(amount) > 0 && !!target && Number(amount) <= target.debt.owes

  return (
    <>
      <Alert status="info" borderRadius="xl" fontSize="sm" mb={4}>
        <AlertIcon />
        <AlertDescription>
          This happens when a passenger is refunded after the company was already paid. <b>Nothing needs to be done:</b> the amount is taken from the company's next earnings automatically, and they cannot withdraw until it is cleared. Record a repayment if they pay you back directly, or write it off.
        </AlertDescription>
      </Alert>
      <Panel flush p={0}>
        {isLoading ? <SkeletonRows /> : !data?.length ? (
          <EmptyState icon={<Check size={26} />} title="Nobody owes Daladala Smart" body="Companies that go below zero after a refund appear here." />
        ) : (
          <TableContainer>
            <Table>
              <Thead><Tr><Th {...tableHead}>Company</Th><Th {...tableHead}>Last paid out</Th><Th {...tableHead}>Last refund</Th><Th {...tableHead} isNumeric>Owes</Th><Th {...tableHead} /></Tr></Thead>
              <Tbody>
                {data.map(d => (
                  <Tr key={d.operatorId} _hover={{ bg: 'gray.50' }}>
                    <Td borderColor="gray.100"><Text fontWeight="medium" fontSize="sm">{d.name}</Text><StatusPill kind="operator" status={d.status} /></Td>
                    <Td borderColor="gray.100" fontSize="sm">{d.lastPaidOutAt ? timeAgo(d.lastPaidOutAt) : '—'}</Td>
                    <Td borderColor="gray.100" fontSize="sm">{d.lastRefundAt ? timeAgo(d.lastRefundAt) : '—'}</Td>
                    <Td borderColor="gray.100" isNumeric><Text fontWeight="semibold" color="red.600" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(d.owes)}</Text></Td>
                    <Td borderColor="gray.100" isNumeric>
                      <HStack justify="flex-end" spacing={2}>
                        <Button size="sm" leftIcon={<Check size={14} />} onClick={() => open(d, 'repayment')}>Record repayment</Button>
                        <Button size="sm" variant="ghost" colorScheme="red" onClick={() => open(d, 'writeoff')}>Write off</Button>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Panel>
      <ConfirmModal
        isOpen={!!target} onClose={() => setTarget(null)} loading={settle.isLoading} tone={target?.mode === 'writeoff' ? 'red' : 'brand'}
        title={target?.mode === 'repayment' ? 'Record a repayment' : 'Write off this debt'} confirmLabel={target?.mode === 'repayment' ? 'Record repayment' : 'Write off'}
        body={target && (
          <VStack align="stretch" spacing={3}>
            <Text>{target.debt.name} owes <b>TZS {formatNumber(target.debt.owes)}</b>.</Text>
            <FormControl isRequired><FormLabel fontSize="sm">Amount (TZS)</FormLabel>
              <Input type="number" min={1} max={target.debt.owes} value={amount} onChange={e => setAmount(e.target.value)} isInvalid={!!amount && !valid} />
              <FormHelperText>Up to TZS {formatNumber(target.debt.owes)}. Part payments are fine.</FormHelperText></FormControl>
          </VStack>
        )}
        inputLabel={target?.mode === 'repayment' ? 'Payment reference' : undefined} inputRequired inputPlaceholder="e.g. mobile-money receipt number"
        reasonLabel={target?.mode === 'writeoff' ? 'Why is this being forgiven?' : 'Note (optional)'} reasonRequired={target?.mode === 'writeoff'}
        onConfirm={({ input, reason }) => { if (valid) settle.mutate({ reference: input || undefined, note: reason || undefined }) }}
      />
    </>
  )
}

function SendBatch({ rows, onClose, onDone }: { rows: PayoutRow[]; onClose: () => void; onDone: () => void }) {
  const toast = useToast()
  const [refs, setRefs] = useState<Record<string, string>>({})
  const items = rows.map(r => ({ id: r.id, reference: (refs[r.id] ?? '').trim() })).filter(i => i.reference)
  const pay = useMutation(() => control.bulkPay(items), {
    onSuccess: r => {
      toast({ title: `${r.data.paid} payment(s) recorded${r.data.failed ? `, ${r.data.failed} could not be recorded` : ''}`, status: r.data.failed ? 'warning' : 'success' })
      onDone(); if (!r.data.failed) onClose()
    },
    onError: e => { toast({ title: apiErrorMessage(e), status: 'error' }) },
  })
  const download = useMutation(() => control.downloadPayoutSheet(), { onError: e => { toast({ title: apiErrorMessage(e), status: 'error' }) } })
  return (
    <Modal isOpen onClose={onClose} isCentered size="3xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(6px)" />
      <ModalContent borderRadius="2xl">
        <ModalHeader pb={1}>Send several payments at once</ModalHeader><ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <Alert status="info" borderRadius="xl" fontSize="sm"><AlertIcon /><AlertDescription>
              <b>1.</b> Download the sheet (account numbers included) and upload it to your bank or mobile-money bulk-payment portal. <b>2.</b> Paste each transaction reference below. Rows left empty stay "ready to send".
            </AlertDescription></Alert>
            <Button alignSelf="flex-start" variant="outline" leftIcon={<Download size={14} />} isLoading={download.isLoading} onClick={() => download.mutate()}>Download payout sheet (CSV)</Button>
            <VStack align="stretch" spacing={2}>
              {rows.map(r => (
                <Flex key={r.id} gap={3} align="center" p={3} border="1px solid" borderColor="gray.100" borderRadius="xl" direction={{ base: 'column', md: 'row' }}>
                  <Box flex="1" minW={0} w="full"><Text fontWeight="medium" fontSize="sm">{r.operator?.name}</Text>
                    <Text fontSize="xs" color="gray.500">{providerLabel(r.destination.provider)} · {r.destination.accountName} · {r.destination.accountMasked}</Text></Box>
                  <Text fontWeight="semibold" sx={{ fontVariantNumeric: 'tabular-nums' }} minW="110px" textAlign="right">TZS {formatNumber(r.amount)}</Text>
                  <Input size="sm" w={{ base: 'full', md: '240px' }} placeholder="Transaction reference" value={refs[r.id] ?? ''} onChange={e => setRefs(s => ({ ...s, [r.id]: e.target.value }))} aria-label={`Reference for ${r.operator?.name}`} />
                </Flex>
              ))}
            </VStack>
          </VStack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button isDisabled={!items.length} isLoading={pay.isLoading} leftIcon={<Send size={14} />} onClick={() => pay.mutate()}>Mark {items.length || ''} as paid</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}


function DestinationModal({ payout, onClose }: { payout: PayoutRow | null; onClose: () => void }) {
  const toast = useToast()
  const { data, isLoading, error } = useQuery(['payout-destination', payout?.id], () => operatorsAdmin.revealDestination(payout!.id).then(r => r.data), { enabled: !!payout, cacheTime: 0, staleTime: 0, retry: false })
  const copy = async (text: string) => { try { await navigator.clipboard.writeText(text); toast({ title: 'Copied', status: 'success', duration: 1500 }) } catch { toast({ title: 'Copy failed', status: 'error' }) } }
  return (
    <Modal isOpen={!!payout} onClose={onClose} isCentered>
      <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(6px)" />
      <ModalContent borderRadius="2xl">
        <ModalHeader>Send the money to</ModalHeader><ModalCloseButton />
        <ModalBody>
          {isLoading ? <SkeletonRows rows={3} h={8} /> : error ? <Text color="red.600" fontSize="sm">{apiErrorMessage(error)}</Text> : data && (
            <VStack align="stretch" spacing={4}>
              <Box><Label>Amount</Label>{payout && <Money value={payout.amount} size="xl" />}</Box>
              <SimpleGrid columns={1} spacing={3}>
                <Box><Label>Network / bank</Label><Text fontWeight="semibold">{providerLabel(data.provider)}</Text></Box>
                <Box><Label>Account name</Label><HStack justify="space-between"><Text fontWeight="semibold">{data.accountName}</Text>
                  <IconButton aria-label="Copy name" size="xs" variant="ghost" icon={<Copy size={14} />} onClick={() => copy(data.accountName)} /></HStack></Box>
                <Box><Label>Account number</Label><HStack justify="space-between"><Text fontWeight="semibold" fontSize="xl" letterSpacing="0.04em" sx={{ fontVariantNumeric: 'tabular-nums' }}>{data.accountNumber}</Text>
                  <IconButton aria-label="Copy number" size="xs" variant="ghost" icon={<Copy size={14} />} onClick={() => copy(data.accountNumber)} /></HStack></Box>
              </SimpleGrid>
              <Alert status="info" borderRadius="xl" fontSize="sm"><AlertIcon />Viewing this is recorded in the audit trail. After sending, come back and enter the transaction reference.</Alert>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter><Button variant="outline" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default function PayoutsPage() {
  const qc = useQueryClient()
  const toast = useToast()
  const [params, setParams] = useSearchParams()
  const fromUrl = params.get('tab') === 'debts' ? 'debts' : params.get('status') === 'approved' ? 'send' : 'review'
  const [tab, setTabState] = useState(fromUrl)
  const setTab = (t: string) => { setTabState(t); setParams({}, { replace: true }) }
  const [batch, setBatch] = useState(false)
  const [dest, setDest] = useState<PayoutRow | null>(null)
  const [act, setAct] = useState<{ payout: PayoutRow; action: Action } | null>(null)
  const statuses = TABS.find(t => t.key === tab)!.statuses
  const { data: owed } = useQuery('admin-debts', () => control.debts().then(r => r.data), { refetchInterval: 30_000 })

  const { data, isLoading } = useQuery(['admin-payouts', tab], async () => {
    const res = await Promise.all(statuses.map(s => operatorsAdmin.payouts({ status: s, limit: 50 })))
    return res.flatMap(r => r.data).sort((a, b) => +new Date(b.requestedAt) - +new Date(a.requestedAt))
  }, { refetchInterval: 30_000, enabled: tab !== 'debts' })
  const { data: counts } = useQuery('admin-payout-counts', async () => {
    const [r, a] = await Promise.all([operatorsAdmin.payouts({ status: 'requested', limit: 100 }), operatorsAdmin.payouts({ status: 'approved', limit: 100 })])
    const sum = (rows: PayoutRow[]) => rows.reduce((s, p) => s + p.amount, 0)
    return { review: r.data.length, reviewAmt: sum(r.data), send: a.data.length, sendAmt: sum(a.data) }
  }, { refetchInterval: 30_000 })

  const mutate = useMutation(({ payout, action, externalRef, reason }: { payout: PayoutRow; action: Action; externalRef?: string; reason?: string }) =>
    operatorsAdmin.payoutAction(payout.id, action, { externalRef, reason }), {
    onSuccess: (_d, v) => {
      toast({ title: { approve: 'Approved. Send the money, then mark it paid.', reject: 'Rejected. Funds returned to the company.', pay: 'Marked as paid', fail: 'Marked as failed. Funds returned.' }[v.action], status: 'success' })
      qc.invalidateQueries('admin-payouts'); qc.invalidateQueries('admin-payout-counts'); qc.invalidateQueries('operators-overview'); setAct(null)
    },
    onError: e => { toast({ title: apiErrorMessage(e), status: 'error' }) },
  })

  const rows = data ?? []
  return (
    <>
      <PageHeader eyebrow="Operators" title="Payouts" subtitle="Review withdrawal requests, send the money, and record the reference." />
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
        <StatTile label="Waiting for review" icon={<Banknote size={20} />} tone="orange" value={counts?.review ?? 0} hint={counts ? <Money value={counts.reviewAmt} size="sm" /> : undefined} loading={!counts} onClick={() => setTab('review')} />
        <StatTile label="Approved, ready to send" icon={<Send size={20} />} tone="blue" value={counts?.send ?? 0} hint={counts ? <Money value={counts.sendAmt} size="sm" /> : undefined} loading={!counts} onClick={() => setTab('send')} />
        <StatTile label="Companies owe us" icon={<Wallet size={20} />} tone={owed?.length ? 'red' : 'gray'} value={owed?.length ?? 0} hint={owed ? <Money value={owed.reduce((s, d) => s + d.owes, 0)} size="sm" /> : undefined} loading={!owed} onClick={() => setTab('debts')} />
      </SimpleGrid>

      <Box overflowX="auto" mb={4} mx={-1} px={1} pb={1}><ButtonGroup isAttached variant="outline" size="sm">
        {TABS.map(t => <Button key={t.key} onClick={() => setTab(t.key)} bg={tab === t.key ? 'gray.900' : 'white'} color={tab === t.key ? 'white' : 'gray.600'} _hover={{ bg: tab === t.key ? 'gray.800' : 'gray.50' }}>{t.label}</Button>)}
      </ButtonGroup></Box>

      {tab === 'send' && rows.length > 1 && (
        <Flex mb={4} p={4} bg="blue.50" border="1px solid" borderColor="blue.100" borderRadius="xl" justify="space-between" align="center" gap={3} direction={{ base: 'column', md: 'row' }}>
          <Text fontSize="sm" color="blue.900"><b>{rows.length} payments</b> are ready. Pay them together from your bank or mobile-money portal, then record the references in one go.</Text>
          <Button size="sm" leftIcon={<Send size={14} />} onClick={() => setBatch(true)}>Send all at once</Button>
        </Flex>
      )}

      {tab === 'debts' ? <DebtsPanel /> : <Panel flush p={0}>
        {isLoading ? <SkeletonRows /> : rows.length === 0 ? (
          <EmptyState icon={<Check size={26} />} title={tab === 'history' ? 'No payout history yet' : 'Nothing waiting here'} body={tab === 'review' ? 'New withdrawal requests from companies appear here.' : undefined} />
        ) : (
          <TableContainer>
            <Table>
              <Thead><Tr><Th {...tableHead}>Company</Th><Th {...tableHead}>Destination</Th><Th {...tableHead}>Status</Th><Th {...tableHead} isNumeric>Amount</Th><Th {...tableHead} /></Tr></Thead>
              <Tbody>
                {rows.map(p => (
                  <Tr key={p.id} _hover={{ bg: 'gray.50' }}>
                    <Td borderColor="gray.100"><Text fontWeight="medium" fontSize="sm">{p.operator?.name}</Text><Text fontSize="xs" color="gray.500">{timeAgo(p.requestedAt)} · {formatDate(p.requestedAt, true)}</Text></Td>
                    <Td borderColor="gray.100"><Text fontSize="sm">{providerLabel(p.destination.provider)}</Text><Text fontSize="xs" color="gray.500">{p.destination.accountName} · {p.destination.accountMasked}</Text></Td>
                    <Td borderColor="gray.100"><StatusPill kind="payout" status={p.status} />{p.externalRef && <Text fontSize="xs" color="gray.500" mt={1}>Ref {p.externalRef}</Text>}{p.failureReason && <Text fontSize="xs" color="red.600" mt={1}>{p.failureReason}</Text>}</Td>
                    <Td borderColor="gray.100" isNumeric><Text fontWeight="semibold" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(p.amount)}</Text></Td>
                    <Td borderColor="gray.100" isNumeric>
                      <HStack justify="flex-end" spacing={2}>
                        {p.status === 'requested' && (<>
                          <Button size="sm" leftIcon={<Check size={14} />} onClick={() => mutate.mutate({ payout: p, action: 'approve' })} isLoading={mutate.isLoading && mutate.variables?.payout.id === p.id}>Approve</Button>
                          <Tooltip label="Reject" hasArrow><IconButton aria-label="Reject" size="sm" variant="ghost" colorScheme="red" icon={<X size={16} />} onClick={() => setAct({ payout: p, action: 'reject' })} /></Tooltip>
                        </>)}
                        {p.status === 'approved' && (<>
                          <Button size="sm" variant="outline" leftIcon={<Eye size={14} />} onClick={() => setDest(p)}>Show account</Button>
                          <Button size="sm" leftIcon={<Send size={14} />} onClick={() => setAct({ payout: p, action: 'pay' })}>Mark paid</Button>
                          <Tooltip label="The transfer did not go through" hasArrow><IconButton aria-label="Mark as failed" size="sm" variant="ghost" colorScheme="red" icon={<X size={16} />} onClick={() => setAct({ payout: p, action: 'fail' })} /></Tooltip>
                        </>)}
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Panel>}

      {batch && <SendBatch rows={rows} onClose={() => setBatch(false)} onDone={() => { qc.invalidateQueries('admin-payouts'); qc.invalidateQueries('admin-payout-counts'); qc.invalidateQueries('control-home') }} />}
      <DestinationModal payout={dest} onClose={() => setDest(null)} />
      <ConfirmModal
        isOpen={!!act} onClose={() => setAct(null)} loading={mutate.isLoading}
        tone={act?.action === 'pay' ? 'brand' : 'red'}
        title={act?.action === 'pay' ? 'Record the payment' : act?.action === 'fail' ? 'Transfer failed' : 'Reject this withdrawal'}
        confirmLabel={act?.action === 'pay' ? 'Mark as paid' : act?.action === 'fail' ? 'Mark as failed' : 'Reject'}
        body={act ? <>{act.payout.operator?.name} · <b>TZS {formatNumber(act.payout.amount)}</b> to {providerLabel(act.payout.destination.provider)} {act.payout.destination.accountMasked}</> : undefined}
        inputLabel={act?.action === 'pay' ? 'Transaction reference' : undefined} inputRequired inputPlaceholder="e.g. mobile-money receipt number" inputHelp="Shown to the company on their statement. A paid payout cannot be undone."
        reasonLabel={act && act.action !== 'pay' ? 'Reason (shown to the company)' : undefined} reasonRequired
        onConfirm={({ input, reason }) => act && mutate.mutate({ payout: act.payout, action: act.action, externalRef: input, reason })}
      />
    </>
  )
}
